import type { ReactNode } from 'react';
import { InfoTip, StatCard } from '@wundo/ui';
import type { SkillStats } from '@wundo/shared/skillStats.ts';

/*
 * The label carries the explanation rather than the card body: these four
 * numbers are each an average of an average, and a reader who does not know
 * what the denominator is cannot use them.
 */
function Label({ children, info }: { children: ReactNode; info: string }) {
  return (
    <span className='inline-flex items-center gap-1'>
      {children}
      <InfoTip content={info} />
    </span>
  );
}

function score(value: number | null): string {
  return value !== null ? value.toFixed(1) : '—';
}

/** Users, win rate, average score and live schedules, over the chosen window. */
export function SkillStatTiles({ stats }: { stats: SkillStats }) {
  const { usage, recommendations, schedules } = stats;

  return (
    <div className='grid grid-cols-2 gap-3.5 mobile:grid-cols-4'>
      <StatCard
        label={
          <Label info='Unique users who had this skill loaded in the selected window. The smaller number below is the all-time count.'>
            Users
          </Label>
        }
        value={usage.unique_users_window}
        hint={`${usage.unique_users_total} all-time`}
      />
      <StatCard
        label={
          <Label info='Share of scoring rounds this skill won — i.e. was picked as the outreach for that user that day. Denominator is the number of times it was scored.'>
            Win rate
          </Label>
        }
        value={
          recommendations.win_rate_pct !== null ? `${recommendations.win_rate_pct}%` : '—'
        }
        hint={`${recommendations.winner_count_window} / ${recommendations.score_count_window} scored`}
      />
      <StatCard
        label={
          <Label info='Average final recommendation score in the window (after boost and freshness factor). The LLM number below is the raw LLM score before those multipliers.'>
            Avg score
          </Label>
        }
        value={score(recommendations.avg_final_score)}
        hint={`LLM ${score(recommendations.avg_llm_score)}`}
      />
      <StatCard
        label={
          <Label info='Currently active reminders plus check-ins scheduled for this skill across all users.'>
            Schedules
          </Label>
        }
        value={schedules.active_reminders + schedules.active_check_ins}
        hint={`${schedules.active_reminders} rem · ${schedules.active_check_ins} chk`}
      />
    </div>
  );
}
